import { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";

import { AuthScreen } from "~/components/composed/AuthScreen";
import { AppText } from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { normalizeAuthRedirectPath } from "~/utils/auth-redirect";
import { getPostAuthPath } from "~/utils/onboarding";

export default function AuthCallbackScreen() {
  const router = useRouter();
  const { session, user, loading, completeEmailLink } = useAuth();
  const params = useLocalSearchParams<{
    token?: string | string[];
    type?: string | string[];
    error_description?: string | string[];
    next?: string | string[];
  }>();
  const errorDescription = Array.isArray(params.error_description)
    ? params.error_description[0]
    : params.error_description;
  const [error, setError] = useState<string | null>(errorDescription ?? null);
  const [exchanging, setExchanging] = useState(false);

  useEffect(() => {
    if (error || loading || exchanging) return;
    const complete = async () => {
      const searchParams =
        typeof window === "undefined"
          ? new URLSearchParams()
          : new URLSearchParams(window.location.search);
      const code =
        searchParams.get("token") ??
        (Array.isArray(params.token) ? params.token[0] : params.token) ??
        undefined;
      const linkType =
        (searchParams.get("type") ??
          (Array.isArray(params.type) ? params.type[0] : params.type)) ===
        "signup"
          ? "signup"
          : "magiclink";
      const next =
        searchParams.get("next") ??
        (Array.isArray(params.next) ? params.next[0] : params.next) ??
        undefined;

      const safeNextPath = normalizeAuthRedirectPath(
        next,
        getPostAuthPath(user),
      );

      if (session?.user && !code) {
        router.replace(safeNextPath as never);
        return;
      }

      if (code) {
        setExchanging(true);
        await completeEmailLink({ token: code, type: linkType });
      }

      router.replace(safeNextPath as never);
    };
    void complete().catch((callbackError: unknown) => {
      setExchanging(false);
      setError(
        callbackError instanceof Error
          ? callbackError.message
          : "Authentication failed.",
      );
    });
  }, [
    error,
    exchanging,
    completeEmailLink,
    loading,
    params.token,
    params.type,
    params.next,
    router,
    session,
    user,
  ]);

  return (
    <AuthScreen title="Signing in">
      {error ? <AppText tone="danger">{error}</AppText> : <ActivityIndicator />}
    </AuthScreen>
  );
}
